<template>
  <div>
    <b-input
      v-model="searchStr"
      :error-messages="getErrorMessages(v$.reviewerIds)"
      prepend-icon="mdi-magnify"
      label="Рецензенти"
      placeholder="Пошук співробітника"
      hide-details="auto"
      @blur="v$.reviewerIds.$touch()"></b-input>

    <div v-if="selectedUsers.length" class="d-flex flex-wrap ga-2 mt-3">
      <v-chip
        v-for="user in selectedUsers"
        :key="user.id"
        closable
        color="light-blue-darken-4"
        variant="tonal"
        @click:close="removeUser(user.id)">
        {{ getPersonName(user, { initials: true }) }}
      </v-chip>
    </div>

    <v-divider class="my-3"></v-divider>

    <b-global-loader :loading="$loading.isLoading('documentReviewUserList')">
      <v-list class="overflow-y-auto" density="compact" max-height="320">
        <v-list-item
          v-for="user in userList"
          :key="user.id"
          class="px-2"
          rounded="lg"
          @click="toggleUser(user)">
          <template #prepend>
            <v-checkbox-btn
              :model-value="isSelected(user.id)"
              color="primary"
              @click.stop="toggleUser(user)"></v-checkbox-btn>
          </template>

          <v-list-item-title class="font-weight-bold">
            {{ getPersonName(user, { initials: true }) }}
          </v-list-item-title>
          <v-list-item-subtitle>
            {{ user.post || 'Посада відсутня' }}
          </v-list-item-subtitle>
        </v-list-item>

        <div v-if="!userList.length" class="text-disabled text-center py-5">
          Дані відсутні
        </div>
      </v-list>
    </b-global-loader>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, shallowRef, watch } from 'vue'
import { Dialog } from '@/dialog'
import { BGlobalLoader } from 'best-modules/components'
import { debounce } from 'lodash'
import { getErrorMessages, getPersonName } from 'best-modules/utils'
import { handleAsync } from 'best-modules/plugins'
import { minLength, required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { userList as userListRequest } from '@/requests/user'
import { documentSendToReview } from '@/requests/document'
import { User } from '@/utils/types/user'

defineOptions({
  name: 'DocumentSendToReview',
})

const props = defineProps<{
  dialog: Dialog
}>()

const searchStr = ref<string>('')
const userList = shallowRef<User[]>([])
const selectedUsers = shallowRef<User[]>([])
const reviewData = reactive({
  reviewerIds: [] as number[],
})

const rules = {
  reviewerIds: { required, minLength: minLength(1) },
}
const v$ = useVuelidate(rules, reviewData)

const isSelected = (userId: number) => {
  return reviewData.reviewerIds.includes(userId)
}

const removeUser = (userId: number) => {
  reviewData.reviewerIds = reviewData.reviewerIds.filter(id => id !== userId)
  selectedUsers.value = selectedUsers.value.filter(user => user.id !== userId)
}

const toggleUser = (user: User) => {
  if (isSelected(user.id)) {
    removeUser(user.id)
    return
  }

  reviewData.reviewerIds.push(user.id)
  selectedUsers.value = [...selectedUsers.value, user]
}

const getUserList = (search: any = null) => {
  return handleAsync('documentReviewUserList', () => {
    return userListRequest({ search }).then(res => {
      userList.value = res.data || []
    })
  })
}

watch(searchStr, debounce(getUserList, 500))

getUserList()

const disabled = computed(() => !reviewData.reviewerIds.length)

const submit = () => {
  return documentSendToReview(props.dialog.dialogData.documentId, {
    reviewerIds: reviewData.reviewerIds,
  })
}

defineExpose({ disabled, submit, v$ })
</script>
